import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import LoadPayBreakdown, { usd } from "../../Loads/LoadPayBreakdown";

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

// Same CSS-variable theme PostLoadPage injects on :root.
const T = {
  surface: "var(--pl-surface)", line: "var(--pl-line)", line2: "var(--pl-line2)", navy: "var(--pl-navy)",
  teal: "var(--pl-teal)", tealLight: "var(--pl-teal-light)", amber: "var(--pl-amber)", amberLight: "var(--pl-amber-light)",
  slate: "var(--pl-slate)", muted: "var(--pl-muted)", text: "var(--pl-text)",
  green: "var(--pl-green)", greenLight: "var(--pl-green-light)", red: "var(--pl-red)", redLight: "var(--pl-red-light)",
};

/* ── Offline fallback only. The server (loads/rates.py) is the source of truth;
      this keeps the form usable if /api/loads/rates/ is unreachable. ──────── */
const FALLBACK_CATALOG = {
  last_reviewed: "offline", basis_note: "Suggested ranges, not regulated rates.", posting_fee: 15, avg_convoy_mph: 45,
  rates: {
    lead_chase_mile: { min: 1.75, max: 2.25, default: 2.0 }, high_pole_mile: { min: 2.25, max: 2.75, default: 2.5 },
    lead_chase_hour: { min: 45, max: 65, default: 55 }, min_dispatch: { min: 250, max: 350, default: 300 },
    detention_hour: { min: 35, max: 65, default: 50 }, overnight_night: { min: 100, max: 175, default: 125 },
    deadhead_mile: { min: 0.75, max: 1.25, default: 0.85 }, night_premium_pct: { min: 15, max: 25, default: 20 },
  },
};

const authHeaders = (token) => ({ "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) });

/* ── hooks ────────────────────────────────────────────────────────────────── */
export function useRateCatalog(token) {
  const [cat, setCat] = useState(FALLBACK_CATALOG);
  useEffect(() => {
    if (!token) return;
    let off = false;
    fetch(`${API}/api/loads/rates/`, { headers: authHeaders(token) })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { if (!off && d?.rates) setCat(d); })
      .catch(() => {});
    return () => { off = true; };
  }, [token]);
  return cat;
}

const toRad = (d) => (d * Math.PI) / 180;
function haversineMi(a, b, c, d) {
  const dLat = toRad(c - a), dLng = toRad(d - b);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a)) * Math.cos(toRad(c)) * Math.sin(dLng / 2) ** 2;
  return 2 * 3958.8 * Math.asin(Math.sqrt(x));
}

/** Routed driving miles via OSRM; falls back to straight-line × 1.18 (labelled as an estimate). */
export function useRouteDistance(oLat, oLng, dLat, dLng) {
  const [state, setState] = useState({ miles: null, source: null, loading: false });
  useEffect(() => {
    if ([oLat, oLng, dLat, dLng].some((v) => v === null || v === undefined || v === "")) {
      setState({ miles: null, source: null, loading: false });
      return;
    }
    const ctrl = new AbortController();
    setState((s) => ({ ...s, loading: true }));
    const fallback = () => ({ miles: Math.round(haversineMi(+oLat, +oLng, +dLat, +dLng) * 1.18 * 10) / 10, source: "estimate", loading: false });
    fetch(`https://router.project-osrm.org/route/v1/driving/${oLng},${oLat};${dLng},${dLat}?overview=false`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((d) => {
        const m = d?.routes?.[0]?.distance;
        setState(m ? { miles: Math.round((m / 1609.344) * 10) / 10, source: "route", loading: false } : fallback());
      })
      .catch((e) => { if (e.name !== "AbortError") setState(fallback()); });
    return () => ctrl.abort();
  }, [oLat, oLng, dLat, dLng]);
  return state;
}

export function effectiveEscorts(form, flags) {
  const f = flags || {};
  const e = {
    front: !!(form.frontEscort || f.needsFront), chase: !!(form.chaseEscort || f.needsChase),
    pole: !!(form.heightPole || f.needsPole), steersman: !!(form.steersman || f.needsSteersman),
  };
  e.auto = {
    front: !form.frontEscort && !!f.needsFront, chase: !form.chaseEscort && !!f.needsChase,
    pole: !form.heightPole && !!f.needsPole, steersman: !form.steersman && !!f.needsSteersman,
  };
  e.count = [e.front, e.chase, e.pole, e.steersman].filter(Boolean).length;
  return e;
}

export function buildEstimateInputs(form, miles, esc) {
  return {
    distance_miles: miles, pay_type: form.payType, fixed_total: form.customPay,
    mileage_rate: form.mileageRate, high_pole_rate: form.highPoleRate,
    hourly_rate: form.hourlyRate, est_hours: form.estHours,
    escorts: { front: esc.front, chase: esc.chase, pole: esc.pole, steersman: esc.steersman },
    min_dispatch: form.minDailyPay, wait_hours: form.waitHours, detention_rate: form.detentionRate,
    nights: form.overnight ? parseInt(form.nights, 10) || 1 : 0, overnight_rate: form.overnightRate,
    deadhead_miles: form.deadheadMiles, deadhead_rate: form.deadheadRate,
    pole_premium: form.heightPolePay, escort_addons: form.escortAddons, night_ops: !!form.nightOps,
  };
}

const EMPTY_EST = { lines: [], total: 0, per_vehicle: null, vehicles: 0, assumptions: [], warnings: [], complete: false, pay: "TBD", pay_sub: "" };

/** Debounced call to the server calculator — the same code that re-runs on save. */
export function useEstimate(inputs, token) {
  const [est, setEst] = useState({ ...EMPTY_EST, loading: false, error: false });
  const key = JSON.stringify(inputs);
  useEffect(() => {
    if (!token) return;
    const ctrl = new AbortController();
    setEst((s) => ({ ...s, loading: true }));
    const t = setTimeout(() => {
      fetch(`${API}/api/loads/estimate/`, { method: "POST", headers: authHeaders(token), body: key, signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((d) => setEst({ ...EMPTY_EST, ...d, loading: false, error: false }))
        .catch((e) => { if (e?.name !== "AbortError") setEst((s) => ({ ...s, loading: false, error: true })); });
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [key, token]);
  return est;
}

/* ── tiny UI primitives (match PostLoadPage styling) ──────────────────────── */
const Lbl = ({ children, hint }) => (
  <div className="flex items-baseline justify-between gap-2 mb-1.5">
    <span style={{ color: T.slate, fontSize: 12, fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase" }}>{children}</span>
    {hint ? <span style={{ color: T.muted, fontSize: 11 }}>{hint}</span> : null}
  </div>
);

function Num({ value, onChange, prefix, suffix, placeholder, ariaLabel }) {
  return (
    <div style={{ position: "relative" }}>
      {prefix && <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.muted, fontSize: 14 }}>{prefix}</span>}
      <input
        inputMode="decimal" aria-label={ariaLabel} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
        style={{ width: "100%", background: T.surface, border: `1.5px solid ${T.line}`, borderRadius: 10, color: T.text, fontSize: 14,
          padding: `12px ${suffix ? 44 : 12}px 12px ${prefix ? 30 : 12}px`, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
        onFocus={(e) => (e.target.style.borderColor = "var(--pl-teal)")} onBlur={(e) => (e.target.style.borderColor = "var(--pl-line)")}
      />
      {suffix && <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: T.muted, fontSize: 12 }}>{suffix}</span>}
    </div>
  );
}

function Chips({ options, value, onPick, fmt }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {options.map((o) => {
        const active = String(value) === String(o.value);
        return (
          <button key={`${o.label}-${o.value}`} type="button" onClick={() => onPick(String(o.value))}
            style={{ fontSize: 11.5, fontWeight: 700, padding: "5px 10px", borderRadius: 99, cursor: "pointer",
              border: `1.5px solid ${active ? T.teal : T.line}`, background: active ? T.tealLight : T.surface, color: active ? T.teal : T.slate }}>
            {o.label} <span style={{ opacity: 0.75, fontWeight: 600 }}>{fmt ? fmt(o.value) : o.value}</span>
          </button>
        );
      })}
    </div>
  );
}

function RangeHint({ value, min, max, unit }) {
  const n = parseFloat(value);
  const suffix = unit.replace("$", "");
  const range = unit === "$/mi" ? `$${min.toFixed(2)}–$${max.toFixed(2)}` : `$${min}–$${max}`;
  if (!value || Number.isNaN(n)) return <div style={{ color: T.muted, fontSize: 11, marginTop: 6 }}>Suggested {range}{suffix}</div>;
  const below = n < min, above = n > max;
  const color = below ? T.amber : above ? T.teal : T.green;
  const msg = below ? "Below suggested range — fewer drivers may apply" : above ? "Above suggested range — expect fast responses" : "Within suggested range";
  return (
    <div style={{ color, fontSize: 11, marginTop: 6, fontWeight: 600 }}>
      ● {msg} <span style={{ color: T.muted, fontWeight: 500 }}>({range}{suffix})</span>
    </div>
  );
}

const Panel = ({ title, icon, children }) => (
  <div className="rounded-xl p-3 sm:p-4 mb-3 sm:mb-4" style={{ background: T.line2, border: `1px solid ${T.line}` }}>
    {title && (
      <div className="flex items-center gap-2 mb-3">
        <span className="w-5 h-5 rounded-md flex items-center justify-center text-[11px]" style={{ background: "color-mix(in srgb, var(--pl-amber) 12%, transparent)", color: T.amber }}>{icon || "$"}</span>
        <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: T.amber }}>{title}</div>
      </div>
    )}
    {children}
  </div>
);

const MODEL_HELP = {
  "Per Mile": "Pay by route mile for each escort vehicle — the most common structure.",
  Hourly: "Pay by the hour, for each escort vehicle. Good for short or urban moves.",
  "Fixed Rate": "One agreed total for the whole job.",
  Negotiable: "Drivers propose their rate. A guide estimate is shown to them.",
};

/* ── live estimate card (used in the form and the sidebar) ─────────────────── */
export function EstimateCard({ est, catalog, miles, milesSource, escCount }) {
  const warnings = est.warnings || [];
  return (
    <div className="rounded-xl p-4" style={{ background: T.surface, border: `1.5px solid ${T.teal}`, boxShadow: "var(--pl-shadow)" }} aria-live="polite">
      <div className="flex items-center justify-between mb-2">
        <div style={{ color: T.teal, fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase" }}>Live estimate</div>
        {est.loading && <span style={{ color: T.muted, fontSize: 11 }}>Updating…</span>}
      </div>
      {est.error && <div style={{ color: T.amber, fontSize: 12, marginBottom: 8 }}>Couldn't reach the rate calculator — your posting is still calculated again when you submit.</div>}
      {!est.lines?.length ? (
        <div style={{ color: T.muted, fontSize: 13, lineHeight: 1.5 }}>
          {miles ? "Enter a rate above to see your estimated cost." : "Choose an origin and destination to calculate route miles, then enter a rate."}
        </div>
      ) : (
        <>
          {est.lines.map((l, i) => (
            <div key={i} className="flex justify-between gap-3 py-1.5" style={{ borderBottom: `1px solid ${T.line2}`, fontSize: 13 }}>
              <span style={{ color: T.slate }} className="min-w-0">
                {l.label}
                {l.unit !== "job" && l.unit !== "flat" && <span style={{ color: T.muted, fontSize: 11 }}> · {l.qty} {l.unit} × {usd(l.rate, 2)}{l.note ? ` ${l.note}` : ""}</span>}
              </span>
              <span style={{ color: T.text, fontWeight: 700 }}>{usd(l.amount, 2)}</span>
            </div>
          ))}
          <div className="flex justify-between items-end pt-3">
            <div>
              <div style={{ color: T.text, fontWeight: 800, fontSize: 14 }}>Estimated total</div>
              {est.per_vehicle && est.vehicles > 1 && <div style={{ color: T.muted, fontSize: 11 }}>{usd(est.per_vehicle, 2)} per vehicle · {est.vehicles} vehicles</div>}
            </div>
            <div style={{ color: T.green, fontWeight: 800, fontSize: 22 }}>{usd(est.total, 2)}</div>
          </div>
        </>
      )}
      {(est.assumptions || []).map((a, i) => <div key={i} style={{ color: T.muted, fontSize: 11, marginTop: 6 }}>ⓘ {a}</div>)}
      {warnings.map((w, i) => <div key={i} style={{ color: w.level === "warn" ? T.amber : T.teal, fontSize: 11.5, marginTop: 6, fontWeight: 600 }}>● {w.text}</div>)}
      <div style={{ color: T.muted, fontSize: 10.5, marginTop: 10, lineHeight: 1.5 }}>
        {miles ? `${Math.round(miles)} mi ${milesSource === "route" ? "(routed)" : "(straight-line estimate × 1.18)"}` : "Route miles pending"}
        {escCount ? ` · ${escCount} escort vehicle${escCount > 1 ? "s" : ""}` : ""}. {catalog.basis_note}
        {catalog.last_reviewed && catalog.last_reviewed !== "offline" ? ` Ranges last reviewed ${catalog.last_reviewed}.` : ""}
      </div>
    </div>
  );
}

/* ── the pay section (drop-in replacement for the old Driver Pay block) ────── */
export function RateCalculatorSection({ form, upd, catalog, miles, milesSource, milesLoading, est, esc, SelectEl }) {
  const R = catalog.rates;
  const pt = form.payType;
  const [extrasOpen, setExtrasOpen] = useState(
    !!(form.waitHours || form.deadheadMiles || form.overnight || form.nightOps || form.heightPolePay || form.escortAddons)
  );

  // Pre-fill suggested defaults once, so the estimate is meaningful immediately.
  useEffect(() => {
    if (pt === "Per Mile" && !form.mileageRate) upd("mileageRate", R.lead_chase_mile.default.toFixed(2));
    if (pt === "Hourly" && !form.hourlyRate) upd("hourlyRate", String(R.lead_chase_hour?.default ?? 55));
    if (!form.minDailyPay) upd("minDailyPay", String(R.min_dispatch.default));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pt, catalog]);

  const escLabels = [esc.front && "Front", esc.chase && "Chase", esc.pole && "High pole", esc.steersman && "Steersman"].filter(Boolean);

  return (
    <>
      {/* basis strip */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="text-[12px] font-bold px-3 py-1.5 rounded-lg" style={{ background: T.tealLight, color: T.teal }}>
          {milesLoading ? "Calculating route…" : miles ? `📍 ${Math.round(miles)} mi ${milesSource === "route" ? "routed" : "(estimate)"}` : "📍 Add origin & destination for miles"}
        </span>
        <span className="text-[12px] font-bold px-3 py-1.5 rounded-lg" style={{ background: T.line2, color: T.slate, border: `1px solid ${T.line}` }}>
          🚗 {esc.count ? `${esc.count} vehicle${esc.count > 1 ? "s" : ""}: ${escLabels.join(", ")}` : "Select escorts above"}
        </span>
        {Object.values(esc.auto || {}).some(Boolean) && (
          <span className="text-[12px] font-bold px-3 py-1.5 rounded-lg" style={{ background: T.amberLight, color: T.amber }}>
            Some escorts added automatically from load dimensions
          </span>
        )}
      </div>

      {/* pay model */}
      <div className="mb-4">
        <Lbl>How will you pay?</Lbl>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Pay model">
          {["Per Mile", "Hourly", "Fixed Rate", "Negotiable"].map((o) => {
            const on = pt === o;
            return (
              <button key={o} type="button" role="radio" aria-checked={on} onClick={() => upd("payType", o)}
                style={{ padding: "11px 8px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer",
                  border: `1.5px solid ${on ? "var(--pl-navy)" : T.line}`, background: on ? T.navy : T.surface, color: on ? "#fff" : T.slate }}>
                {o}
              </button>
            );
          })}
        </div>
        <div style={{ color: T.muted, fontSize: 12, marginTop: 6 }}>{MODEL_HELP[pt]}</div>
      </div>

      {/* model-specific inputs */}
      {pt === "Per Mile" && (
        <Panel title="Mileage rates">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Lbl>Lead / chase ($ per mile)</Lbl>
              <Num value={form.mileageRate} onChange={(v) => upd("mileageRate", v)} prefix="$" suffix="/mi" placeholder="2.00" ariaLabel="Lead or chase rate per mile" />
              <Chips value={form.mileageRate} onPick={(v) => upd("mileageRate", v)} fmt={(v) => `$${(+v).toFixed(2)}`}
                options={[{ label: "Low", value: R.lead_chase_mile.min.toFixed(2) }, { label: "Suggested", value: R.lead_chase_mile.default.toFixed(2) }, { label: "High", value: R.lead_chase_mile.max.toFixed(2) }]} />
              <RangeHint value={form.mileageRate} min={R.lead_chase_mile.min} max={R.lead_chase_mile.max} unit="$/mi" />
            </div>
            {esc.pole ? (
              <div>
                <Lbl>High pole ($ per mile)</Lbl>
                <Num value={form.highPoleRate} onChange={(v) => upd("highPoleRate", v)} prefix="$" suffix="/mi" placeholder={R.high_pole_mile.default.toFixed(2)} ariaLabel="High pole rate per mile" />
                <Chips value={form.highPoleRate} onPick={(v) => upd("highPoleRate", v)} fmt={(v) => `$${(+v).toFixed(2)}`}
                  options={[{ label: "Low", value: R.high_pole_mile.min.toFixed(2) }, { label: "Suggested", value: R.high_pole_mile.default.toFixed(2) }, { label: "High", value: R.high_pole_mile.max.toFixed(2) }]} />
                <RangeHint value={form.highPoleRate} min={R.high_pole_mile.min} max={R.high_pole_mile.max} unit="$/mi" />
              </div>
            ) : null}
          </div>
        </Panel>
      )}

      {pt === "Hourly" && (
        <Panel title="Hourly rates">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Lbl>Rate ($ per hour, per vehicle)</Lbl>
              <Num value={form.hourlyRate} onChange={(v) => upd("hourlyRate", v)} prefix="$" suffix="/hr" placeholder="55" ariaLabel="Hourly rate" />
              <Chips value={form.hourlyRate} onPick={(v) => upd("hourlyRate", v)} fmt={(v) => `$${v}`}
                options={[{ label: "Low", value: R.lead_chase_hour?.min ?? 45 }, { label: "Suggested", value: R.lead_chase_hour?.default ?? 55 }, { label: "High", value: R.lead_chase_hour?.max ?? 65 }]} />
              <RangeHint value={form.hourlyRate} min={R.lead_chase_hour?.min ?? 45} max={R.lead_chase_hour?.max ?? 65} unit="$/hr" />
            </div>
            <div>
              <Lbl hint={miles ? `≈ ${(miles / (catalog.avg_convoy_mph || 45)).toFixed(1)} h at ${catalog.avg_convoy_mph || 45} mph` : ""}>Billable hours</Lbl>
              <Num value={form.estHours} onChange={(v) => upd("estHours", v)} suffix="hrs" placeholder={miles ? (miles / (catalog.avg_convoy_mph || 45)).toFixed(1) : "e.g. 8"} ariaLabel="Estimated billable hours" />
              <div style={{ color: T.muted, fontSize: 11, marginTop: 6 }}>Leave blank to estimate from route miles.</div>
            </div>
          </div>
        </Panel>
      )}

      {pt === "Fixed Rate" && (
        <Panel title="Fixed total">
          <Lbl>Total for the whole job (all escort vehicles)</Lbl>
          <Num value={form.customPay} onChange={(v) => { upd("customPay", v); upd("pay", v ? "Custom Amount" : ""); }} prefix="$" placeholder="e.g. 1,250" ariaLabel="Fixed total amount" />
          <Chips value={form.customPay} onPick={(v) => { upd("customPay", v); upd("pay", "Custom Amount"); }} fmt={(v) => `$${(+v).toLocaleString()}`}
            options={[500, 1000, 1500, 2500].map((v) => ({ label: "", value: v }))} />
        </Panel>
      )}

      {pt === "Negotiable" && (
        <Panel title="Negotiable">
          <div style={{ color: T.slate, fontSize: 13, lineHeight: 1.6 }}>
            Drivers will see "Negotiable" and propose their own rate or accept a guide based on the suggested ranges
            {est.lines?.length ? ` (about ${usd(est.total, 0)} for this job).` : "."}
          </div>
        </Panel>
      )}

      {/* minimum + extras */}
      <Panel title="Minimum & extras" icon="+">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Lbl hint="per vehicle">Minimum dispatch</Lbl>
            <Num value={form.minDailyPay} onChange={(v) => upd("minDailyPay", v)} prefix="$" placeholder={String(R.min_dispatch.default)} ariaLabel="Minimum dispatch per vehicle" />
            <RangeHint value={form.minDailyPay} min={R.min_dispatch.min} max={R.min_dispatch.max} unit="$" />
          </div>
          <div>
            <Lbl>Fuel surcharge</Lbl>
            {SelectEl ? SelectEl : null}
          </div>
        </div>

        <button type="button" onClick={() => setExtrasOpen((o) => !o)} aria-expanded={extrasOpen}
          style={{ marginTop: 14, fontSize: 12.5, fontWeight: 700, color: T.teal, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          {extrasOpen ? "▾" : "▸"} Waiting, overnight, deadhead & premiums
        </button>

        {extrasOpen && (
          <div className="mt-3 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Lbl>Expected waiting (hours)</Lbl>
                <Num value={form.waitHours} onChange={(v) => upd("waitHours", v)} suffix="hrs" placeholder="0" ariaLabel="Expected waiting hours" />
              </div>
              <div>
                <Lbl>Detention rate</Lbl>
                <Num value={form.detentionRate} onChange={(v) => upd("detentionRate", v)} prefix="$" suffix="/hr" placeholder={String(R.detention_hour.default)} ariaLabel="Detention rate per hour" />
                <Chips value={form.detentionRate} onPick={(v) => upd("detentionRate", v)} fmt={(v) => `$${v}`}
                  options={[{ label: "", value: R.detention_hour.min }, { label: "", value: R.detention_hour.default }, { label: "", value: R.detention_hour.max }]} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Lbl>Deadhead miles</Lbl>
                <Num value={form.deadheadMiles} onChange={(v) => upd("deadheadMiles", v)} suffix="mi" placeholder="0" ariaLabel="Deadhead miles" />
              </div>
              <div>
                <Lbl>Deadhead rate</Lbl>
                <Num value={form.deadheadRate} onChange={(v) => upd("deadheadRate", v)} prefix="$" suffix="/mi" placeholder={R.deadhead_mile.default.toFixed(2)} ariaLabel="Deadhead rate per mile" />
              </div>
            </div>

            <label className="flex items-center gap-3 cursor-pointer" style={{ fontSize: 13.5, color: T.text, fontWeight: 600 }}>
              <input type="checkbox" checked={!!form.overnight} onChange={(e) => upd("overnight", e.target.checked)} />
              Overnight stay required
            </label>
            {form.overnight && (
              <div className="grid grid-cols-2 gap-4">
                <div><Lbl>Nights</Lbl><Num value={form.nights} onChange={(v) => upd("nights", v)} placeholder="1" ariaLabel="Number of nights" /></div>
                <div>
                  <Lbl>Rate per night</Lbl>
                  <Num value={form.overnightRate} onChange={(v) => upd("overnightRate", v)} prefix="$" placeholder="125" ariaLabel="Overnight rate" />
                  <Chips value={form.overnightRate} onPick={(v) => upd("overnightRate", v)} fmt={(v) => `$${v}`}
                    options={[{ label: "", value: R.overnight_night.min }, { label: "", value: R.overnight_night.default }, { label: "", value: R.overnight_night.max }]} />
                </div>
              </div>
            )}

            <label className="flex items-center gap-3 cursor-pointer" style={{ fontSize: 13.5, color: T.text, fontWeight: 600 }}>
              <input type="checkbox" checked={!!form.nightOps} onChange={(e) => upd("nightOps", e.target.checked)} />
              Night operation (+{R.night_premium_pct.default}% on mileage/hourly)
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Lbl>High-pole premium</Lbl><Num value={form.heightPolePay} onChange={(v) => upd("heightPolePay", v)} prefix="$" placeholder="e.g. 50" ariaLabel="High pole premium" /></div>
              <div><Lbl>Other add-ons</Lbl><Num value={form.escortAddons} onChange={(v) => upd("escortAddons", v)} prefix="$" placeholder="e.g. 75" ariaLabel="Other add-ons" /></div>
            </div>
          </div>
        )}
      </Panel>

      <EstimateCard est={est} catalog={catalog} miles={miles} milesSource={milesSource} escCount={esc.count} />
    </>
  );
}

/* ── review & confirm ─────────────────────────────────────────────────────── */
function previewLoadFromForm(form, est, miles, esc) {
  return {
    pay: est.pay, pay_sub: est.pay_sub, pay_type: form.payType,
    mileage_rate: form.payType === "Per Mile" ? form.mileageRate : "", hourly_rate: form.payType === "Hourly" ? form.hourlyRate : "",
    min_daily_pay: form.minDailyPay, height_pole_pay: form.heightPolePay, escort_addons: form.escortAddons,
    detention_rate: form.detentionRate, deadhead_rate: form.deadheadRate,
    overnight: !!form.overnight, overnight_rate: form.overnightRate, fuel_surcharge: form.fuelSurcharge,
    payment_method: form.paymentMethod, payment_terms: form.paymentTerms,
    est_total: est.lines?.length ? est.total : null, distance_miles: miles,
    pay_breakdown: { lines: est.lines, assumptions: est.assumptions, vehicles: est.vehicles },
  };
}

function Item({ label, value }) {
  if (!value) return null;
  return (
    <div className="py-2" style={{ borderBottom: `1px solid ${T.line2}` }}>
      <div style={{ color: T.muted, fontSize: 10.5, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: T.text, fontSize: 14, fontWeight: 600, marginTop: 2, wordBreak: "break-word" }}>{value}</div>
    </div>
  );
}

const Block = ({ title, children }) => (
  <section className="rounded-2xl p-4 sm:p-5" style={{ background: T.surface, border: `1px solid ${T.line}` }}>
    <h3 style={{ color: T.teal, fontSize: 10.5, fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 6 }}>{title}</h3>
    {children}
  </section>
);

export function ReviewPostModal({ form, featured, est, miles, milesSource, esc, escortFlags, catalog, fee, submitting, onBack, onConfirm, mapEl }) {
  const [ack, setAck] = useState(false);

  const blockers = [];
  if (!form.origin) blockers.push("Origin is required.");
  if (!form.destination) blockers.push("Destination is required.");
  if (!form.date) blockers.push("Pickup date is required.");
  if (!esc.count) blockers.push("Select at least one required escort vehicle.");
  if (form.payType !== "Negotiable" && !est.lines?.length) blockers.push("Enter a pay rate so drivers can see what they'll earn.");

  const warnings = [];
  (est.warnings || []).forEach((w) => warnings.push(w.text));
  if (!form.time) warnings.push("No pickup time set — drivers will see the date only.");
  if (!form.meetingPoint) warnings.push("No meeting point set — drivers won't know where to meet the load.");
  if (!form.width && !form.height) warnings.push("Width and height are blank — drivers can't judge equipment needs.");
  if (!form.dispatchPhone) warnings.push("No dispatch phone — drivers can't call you once they're confirmed.");
  if (miles && milesSource !== "route") warnings.push("Route distance is a straight-line estimate, not a routed figure.");
  (escortFlags?.warnings || []).filter((w) => w.level === "high").forEach((w) => warnings.push(w.text));

  const preview = useMemo(() => previewLoadFromForm(form, est, miles, esc), [form, est, miles, esc]);
  const escLabels = [
    esc.front && `Front escort${esc.auto.front ? " (auto-added from dimensions)" : ""}`,
    esc.chase && `Chase escort${esc.auto.chase ? " (auto-added from dimensions)" : ""}`,
    esc.pole && `High pole${esc.auto.pole ? " (auto-added from dimensions)" : ""}`,
    esc.steersman && `Steersman${esc.auto.steersman ? " (auto-added from dimensions)" : ""}`,
  ].filter(Boolean);
  const dims = [form.width, form.height, form.length && `${form.length} long`].filter(Boolean).join(" × ");
  const canPost = blockers.length === 0 && ack && !submitting;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onBack();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onBack, submitting]);

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center sm:p-4" style={{ background: "rgba(15,23,42,0.6)", backdropFilter: "blur(3px)" }}
      role="dialog" aria-modal="true" aria-label="Review and confirm your load posting">
      <div className="w-full sm:max-w-4xl flex flex-col overflow-hidden sm:rounded-2xl rounded-t-2xl shadow-2xl" style={{ background: "var(--pl-bg)", maxHeight: "94vh" }}>
        {/* header */}
        <div className="px-5 py-4 flex items-center justify-between gap-3" style={{ background: "var(--pl-navy)" }}>
          <div className="min-w-0">
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 10.5, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase" }}>Step 2 of 2 · Review</div>
            <div style={{ color: "#fff", fontSize: 18, fontWeight: 800 }} className="truncate">Confirm your load before it goes live</div>
          </div>
          <button type="button" onClick={onBack} disabled={submitting} aria-label="Close review"
            style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(255,255,255,0.12)", color: "#fff", border: "none", cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {blockers.length > 0 && (
            <div className="rounded-xl p-4" style={{ background: T.redLight, border: `1px solid ${T.red}40` }} role="alert">
              <div style={{ color: T.red, fontWeight: 800, fontSize: 13, marginBottom: 4 }}>Fix before posting</div>
              <ul style={{ color: T.red, fontSize: 13, paddingLeft: 18, listStyle: "disc" }}>{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
            </div>
          )}
          {warnings.length > 0 && (
            <div className="rounded-xl p-4" style={{ background: T.amberLight, border: `1px solid ${T.amber}40` }}>
              <div style={{ color: T.amber, fontWeight: 800, fontSize: 13, marginBottom: 4 }}>Worth a second look (you can still post)</div>
              <ul style={{ color: "#92400e", fontSize: 13, paddingLeft: 18, listStyle: "disc" }}>{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Block title="Route & schedule">
              {mapEl ? (
                <div className="mb-3">
                  {mapEl}
                  <div style={{ color: T.muted, fontSize: 11, marginTop: 6 }}>Check that both pins sit on the right cities — drivers see this same map.</div>
                </div>
              ) : null}
              <Item label="Origin" value={`${form.origin}${form.originZip ? ` ${form.originZip}` : ""}`} />
              <Item label="Destination" value={`${form.destination}${form.destinationZip ? ` ${form.destinationZip}` : ""}`} />
              <Item label="Distance" value={miles ? `${Math.round(miles)} mi ${milesSource === "route" ? "(routed)" : "(estimate)"}` : ""} />
              <Item label="Pickup" value={[form.date, form.time].filter(Boolean).join(" · ")} />
              <Item label="Meeting point" value={form.meetingPoint ? `${form.meetingPoint}${form.meetingTime ? ` · ${form.meetingTime}` : ""}` : ""} />
            </Block>

            <Block title="Load & escorts">
              <Item label="Commodity" value={[form.loadType, form.commodity].filter(Boolean).join(" — ")} />
              <Item label="Dimensions" value={dims} />
              <Item label="Weight" value={form.weight ? `${form.weight} lbs` : ""} />
              <Item label="Escort vehicles" value={escLabels.length ? `${esc.count}: ${escLabels.join(", ")}` : ""} />
              <Item label="Dimension verification" value={[form.escortConfirmed && "Escort confirmed", form.truckerConfirmed && "Trucker confirmed"].filter(Boolean).join(" · ")} />
            </Block>

            <Block title="Dispatch contact (shared after a driver is confirmed)">
              <Item label="Company" value={form.companyName} />
              <Item label="Dispatcher" value={form.dispatcherName} />
              <Item label="Phone" value={form.dispatchPhone} />
              <Item label="Email" value={form.dispatchEmail} />
              <Item label="Records copy" value={form.recordsEmail} />
            </Block>

            <Block title="Notes">
              <div style={{ color: T.text, fontSize: 14, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{form.notes || <span style={{ color: T.muted }}>No special instructions.</span>}</div>
            </Block>
          </div>

          <div>
            <h3 style={{ color: T.teal, fontSize: 10.5, fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", margin: "4px 0 8px" }}>
              Exactly what drivers will see for pay
            </h3>
            <LoadPayBreakdown load={preview} variant="company" />
          </div>

          <div className="rounded-xl p-4 flex justify-between items-center" style={{ background: T.surface, border: `1px solid ${T.line}` }}>
            <div>
              <div style={{ color: T.text, fontWeight: 700, fontSize: 14 }}>Standard lead fee{featured ? " + Featured listing" : ""}</div>
              <div style={{ color: T.muted, fontSize: 12 }}>Marketplace posting fee</div>
            </div>
            <div style={{ color: T.teal, fontWeight: 800, fontSize: 20 }}>${fee}.00</div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer rounded-xl p-4" style={{ background: T.surface, border: `1.5px solid ${ack ? T.teal : T.line}` }}>
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} style={{ marginTop: 3 }} />
            <span style={{ color: T.text, fontSize: 13.5, lineHeight: 1.55 }}>
              I confirm these details are accurate and match the permit, and I understand drivers will see the pay terms shown above.
            </span>
          </label>
        </div>

        {/* footer */}
        <div className="px-5 py-4 flex flex-col-reverse sm:flex-row sm:justify-between gap-3" style={{ background: T.surface, borderTop: `1px solid ${T.line}` }}>
          <button type="button" onClick={onBack} disabled={submitting}
            style={{ padding: "12px 20px", borderRadius: 12, border: `1.5px solid ${T.line}`, background: T.surface, color: T.slate, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
            ← Edit details
          </button>
          <button type="button" onClick={onConfirm} disabled={!canPost}
            style={{ padding: "12px 24px", borderRadius: 12, border: "none", fontWeight: 800, fontSize: 14, cursor: canPost ? "pointer" : "not-allowed",
              background: canPost ? "var(--pl-teal)" : "var(--pl-muted-bg)", color: canPost ? "#fff" : T.muted }}>
            {submitting ? "Posting…" : "Confirm & post load"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
